import React, { useEffect, useRef, useState } from 'react';
import { Slider } from '@mui/material';
import Button from '../../../../../components/Button';
import MuiTooltip from '../../../../../components/MuiComponents/MuiTooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useValidateLogin } from '../../../../../hooks/auth';
import FeatureLockIcon from '../../../../../components/Billing/FeatureLockIcon';
import { FEATURE_KEYS, isFeatureLocked } from '../../../../../utils/constants/featureAccess';

const THROTTLING_PRESETS = {
  stealth: { rateLimit: 10, concurrency: 1, requestTimeout: 10 },
  standard: { rateLimit: 80, concurrency: 10, requestTimeout: 5 },
  turbo: { rateLimit: 120, concurrency: 20, requestTimeout: 3 },
};

const getGaugeInfo = (rate) => {
  if (rate >= 1 && rate <= 29) {
    return {
      label: 'Slow',
      color: 'green',
      description: 'Your test will run slowly and carefully, ideal for production',
    };
  } else if (rate >= 30 && rate <= 89) {
    return {
      label: 'Balanced',
      color: 'blue',
      description: 'Your test will run at a balanced pace with moderate speed',
    };
  } else if (rate >= 90 && rate <= 120) {
    return {
      label: 'Fast',
      color: 'orange',
      description: 'Your test will run fast but may trigger rate-limits',
    };
  } else if (rate >= 121 && rate <= 150) {
    return {
      label: 'Intense',
      color: 'red',
      description: 'Your test will run intensely and will likely trigger rate-limits',
    };
  }
  return {
    label: 'Intense',
    color: 'red',
    description: 'Your test will run intensely and will likely trigger rate-limits',
  };
};

const getGaugeImagePath = (rateLimit) => {
  if (rateLimit >= 1 && rateLimit <= 29) return '/images/genpt/throttle/slow.svg';
  if (rateLimit >= 30 && rateLimit <= 89) return '/images/genpt/throttle/standard.svg';
  if (rateLimit >= 90 && rateLimit <= 120) return '/images/genpt/throttle/fast.svg';
  return '/images/genpt/throttle/intense.svg';
};

const SLIDER_SX = {
  color: '#12AFB8',
  height: 10,
  '& .MuiSlider-thumb': {
    backgroundColor: 'white',
    border: '1px solid #12AFB8',
    width: 12,
    height: 12,
    '&:hover': {
      boxShadow: '0 0 0 6px rgba(18, 175, 184, 0.16)',
    },
  },
  '& .MuiSlider-track': { height: 10 },
  '& .MuiSlider-rail': { height: 10 },
  '&.Mui-disabled': { color: '#12AFB8', opacity: 0.5 },
};

const STRATEGY_TOOLTIPS = {
  stealth: (
    <div>
      <div className="font-semibold text-[14px] mb-1">Ideal for Production Environments</div>
      <ul className="list-disc list-inside text-xs">
        <li>Throttles requests to stay below strict detection thresholds.</li>
        <li>Minimizes request density to prevent triggering automated blocking rules on sensitive assets</li>
      </ul>
    </div>
  ),
  standard: (
    <div>
      <div className="font-semibold text-[14px] mb-1">Ideal for Staging/QA Environments</div>
      <ul className="list-disc list-inside text-xs">
        <li>Maintains a balanced request velocity to ensure timely test completion.</li>
        <li>May trigger alerts on strict firewalls; recommended for whitelisted IPs.</li>
      </ul>
    </div>
  ),
  turbo: (
    <div>
      <div className="font-semibold text-[14px] mb-1">Not Ideal for Standard Environments</div>
      <ul className="list-disc list-inside text-xs">
        <li>Excessive request volume sent to target assets will likely trigger the WAF to lock out our IP.</li>
        <li>Ensure rate-limiting rules are disabled or bypassed.</li>
      </ul>
    </div>
  ),
};

/**
 * Renders the description text with colored keywords.
 */
const GaugeDescription = ({ rateLimit }) => {
  const description = getGaugeInfo(rateLimit).description;
  const parts = description.split(/(slowly|balanced|fast|intensely)/i);
  const colorMap = {
    slowly: '#3ADA7A',
    balanced: '#FA890E',
    fast: '#F65C5C',
    intensely: '#BA0000',
  };

  return parts.map((part, index) => {
    const color = colorMap[part.toLowerCase()];
    if (color) {
      return (
        <span key={index} className="font-semibold" style={{ color }}>
          {part}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

/**
 * ThrottlingSetup - Reusable component for throttling configuration.
 *
 * Props:
 *   initialValues  - { testStrategy, rateLimit, concurrency, requestTimeout }
 *   isReadOnly     - Render in read-only (display) mode
 *   isDisabled     - Disable all interactions
 *   onContinue     - Callback with config_settings (Continue in schedule-only modal; or "Start & Schedule" when paired with Start Test)
 *   onStartTest    - Optional. When provided (and scheduleOnly is false), two buttons: "Start Test" and "Start & Schedule"
 *   scheduleOnly   - When true (e.g. Schedule GenPT modal), hide "Start Test" and show Continue → schedule step.
 */
function ThrottlingSetup({
  initialValues = {},
  isReadOnly = false,
  isDisabled = false,
  onContinue = () => {},
  onStartTest = null,
  scheduleOnly = false,
  primaryBtnText = 'CONTINUE',
}) {
  const [testStrategy, setTestStrategy] = useState(initialValues.testStrategy || 'standard');
  const [rateLimit, setRateLimit] = useState(initialValues.rateLimit || 80);
  const [concurrency, setConcurrency] = useState(initialValues.concurrency || 10);
  const [requestTimeout, setRequestTimeout] = useState(initialValues.requestTimeout || 5);
  const [showHighRateModal, setShowHighRateModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Restore from initialValues once when they change
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    const iv = initialValues;
    if (!iv || Object.keys(iv).length === 0) return;

    if (iv.rateLimit !== undefined) setRateLimit(iv.rateLimit);
    if (iv.concurrency !== undefined) setConcurrency(iv.concurrency);
    if (iv.requestTimeout !== undefined) setRequestTimeout(iv.requestTimeout);

    if (iv.testStrategy) {
      setTestStrategy(iv.testStrategy);
    } else if (iv.rateLimit !== undefined) {
      // Auto-detect preset from values
      let matched = false;
      for (const [presetName, preset] of Object.entries(THROTTLING_PRESETS)) {
        if (
          iv.rateLimit === preset.rateLimit &&
          iv.concurrency === preset.concurrency &&
          iv.requestTimeout === preset.requestTimeout
        ) {
          setTestStrategy(presetName);
          matched = true;
          break;
        }
      }
      if (!matched) setTestStrategy('custom');
    }

    hasInitialized.current = true;
  }, [initialValues]);

  const handlePresetSelect = (preset) => {
    if (preset === 'custom') {
      setTestStrategy('custom');
      return;
    }
    const presetValues = THROTTLING_PRESETS[preset];
    if (presetValues) {
      setTestStrategy(preset);
      setRateLimit(presetValues.rateLimit);
      setConcurrency(presetValues.concurrency);
      setRequestTimeout(presetValues.requestTimeout);
    }
  };

  const handleRateLimitChange = (newValue) => {
    if (newValue < 10) return;
    if (testStrategy !== 'custom') setTestStrategy('custom');
    setRateLimit(newValue);
  };

  const handleConcurrencyChange = (newValue) => {
    if (newValue < 1) return;
    if (testStrategy !== 'custom') setTestStrategy('custom');
    setConcurrency(newValue);
  };

  const handleTimeoutChange = (newValue) => {
    if (newValue < 1) return;
    if (testStrategy !== 'custom') setTestStrategy('custom');
    setRequestTimeout(newValue);
  };

  const buildConfigSettings = () => ({
    vulnerability: {
      rate_limit: rateLimit,
      concurrency,
      timeout: requestTimeout,
      retries: 2,
    },
    validation: {
      timeout: requestTimeout,
      retries: 2,
    },
    strategy: testStrategy,
  });

  const executeWithHighRateCheck = (action) => {
    if (testStrategy === 'turbo' || rateLimit > 120) {
      setShowHighRateModal(true);
      setPendingAction(() => action);
      return;
    }
    action();
  };

  const handleContinueClick = () => {
    executeWithHighRateCheck(() => onContinue(buildConfigSettings()));
  };

  const handleStartTestClick = () => {
    executeWithHighRateCheck(() => onStartTest(buildConfigSettings()));
  };

  const allDisabled = isReadOnly || isDisabled;
  const buttonsDisabled = isDisabled || rateLimit < 10 || concurrency < 1 || requestTimeout < 1;

  // ROAR-5007 (AC11): "Start & Schedule" leads into the schedule step, so it locks when the plan has
  // no genpt_scheduling; "Start Test" stays available. The API rejects the schedule POST regardless.
  const { data: loginData } = useValidateLogin();
  const schedulingLocked = isFeatureLocked(
    loginData?.user?.features,
    FEATURE_KEYS.GENPT_SCHEDULING
  );
  const scheduleButtonDisabled = buttonsDisabled || schedulingLocked;

  return (
    <div className={allDisabled ? 'opacity-75 pointer-events-none' : ''}>
      <div className="space-y-6">
        {/* Please Note Section */}
        <div className="flex items-start gap-1">
          <LightbulbOutlinedIcon
            sx={{ color: '#F97316', fontSize: '20px' }}
            className="flex-shrink-0 mt-0.5"
          />
          <div className="text-sm">
            <span className="font-semibold" style={{ color: '#F97316' }}>
              Please Note:
            </span>{' '}
            <span className="text-gray-800">
              Exceeding your server's traffic capacity or WAF thresholds can
              trigger blocking, leading to failed requests or an incomplete
              test. If you are unsure of your asset's limits, we recommend
              starting with <strong>Standard</strong>.
            </span>
          </div>
        </div>

        {/* Test Strategy Section */}
        <div
          className="rounded-lg p-5"
          style={{ background: '#12AFB80D', border: '1px solid #12AFB8' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <SettingsIcon className="text-primary-500" />
            <div className="tracking-wide font-semibold text-black">
              Test Strategy
            </div>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            Choose a preset configuration or customize individual parameters
            to optimize your test flow.
          </div>
          <div className="flex flex-col md:flex-row flex-wrap gap-3">
            {['stealth', 'standard', 'turbo', 'custom'].map((strategy) => {
              const buttonContent = (
                <button
                  onClick={() => !allDisabled && handlePresetSelect(strategy)}
                  disabled={allDisabled}
                  className={`flex-1 min-w-0 px-3 py-2 rounded-lg border-2 transition-all flex flex-col items-center justify-center gap-1.5 ${
                    testStrategy === strategy
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-white border-gray-300 hover:border-primary-300'
                  } ${allDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={
                    testStrategy !== strategy ? { color: '#00000099' } : {}
                  }
                >
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <img
                      src={`/images/genpt/throttle/icons/${strategy === 'custom' ? 'radar' : strategy}.svg`}
                      alt={strategy}
                      className={`w-[18px] h-[18px] object-contain ${
                        testStrategy === strategy
                          ? 'brightness-0 invert'
                          : 'grayscale opacity-70'
                      }`}
                    />
                  </div>
                  <span className="capitalize font-medium text-center text-sm">
                    {strategy}
                  </span>
                </button>
              );

              if (strategy === 'custom') {
                return (
                  <React.Fragment key={strategy}>
                    {buttonContent}
                  </React.Fragment>
                );
              }

              return (
                <MuiTooltip key={strategy} title={STRATEGY_TOOLTIPS[strategy]}>
                  {buttonContent}
                </MuiTooltip>
              );
            })}
          </div>
        </div>

        {/* Expected Outcome Section */}
        <div className="text-sm text-gray-600">
          Based on your current settings, here's what to expect from this test
          run.
        </div>
        <div className="border border-gray-200 rounded-lg p-5 bg-white">
          <div className="flex flex-col items-center">
            <div className="w-full mb-4 relative flex items-center justify-center">
              <div className="relative w-full max-w-md flex flex-col items-center">
                <img
                  src={getGaugeImagePath(rateLimit)}
                  alt={getGaugeInfo(rateLimit).label}
                  className="w-full max-w-[200px] h-auto"
                />
              </div>
            </div>
            <div
              className={`text-sm text-center ${
                getGaugeInfo(rateLimit).color === 'red'
                  ? 'text-red-600'
                  : 'text-gray-700'
              }`}
            >
              <GaugeDescription rateLimit={rateLimit} />
            </div>
          </div>
        </div>

        {/* Advanced Parameters Section */}
        <div className="space-y-4">
          <div className="text-lg font-semibold text-gray-800">
            Advanced Parameters
          </div>

          {/* Rate Limit */}
          <div className="py-0.5 px-2 bg-white">
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2">
                <img
                  src="/images/genpt/throttle/icons/rate_limit.svg"
                  alt="Rate limit"
                  className="w-4 h-4 object-contain flex-shrink-0"
                />
                <div className="font-semibold text-black">Rate Limit</div>
              </div>
              <div className="font-semibold !text-[16px] text-gray-700 bg-[#F6F9FC] px-2 py-1">
                <span style={{ fontWeight: 700 }}>{rateLimit}</span>{' '}
                <span className="text-sm" style={{ fontWeight: 300 }}>
                  req/s
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-1">
              Maximum number of requests sent per second.
            </div>
            <div>
              <Slider
                value={rateLimit}
                onChange={(e, newValue) => handleRateLimitChange(newValue)}
                min={10}
                max={150}
                valueLabelDisplay="off"
                disabled={allDisabled}
                className="w-full"
                sx={SLIDER_SX}
              />
            </div>
            <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
              <div className="flex items-start gap-2">
                <img
                  src="/images/genpt/throttle/icons/info.svg"
                  alt="Info"
                  className="w-4 h-4 object-contain flex-shrink-0 mt-0.5"
                />
                <div className="text-xs text-gray-700">
                  Higher limits increase speed but may trigger rate limits or
                  overload the target server. Lower limits generally ensure
                  uninterrupted tests.
                </div>
              </div>
            </div>
          </div>

          {/* Concurrency */}
          <div className="py-0.5 px-2 bg-white">
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2">
                <img
                  src="/images/genpt/throttle/icons/concurrency.svg"
                  alt="Concurrency"
                  className="w-4 h-4 object-contain flex-shrink-0"
                />
                <div className="font-semibold text-black">Concurrency</div>
              </div>
              <div className="text-sm text-gray-700 bg-[#F6F9FC] px-2 py-1">
                <span style={{ fontWeight: 700 }}>{concurrency}</span>
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-1">
              Number of test cases in parallel.
            </div>
            <div>
              <Slider
                value={concurrency}
                onChange={(e, newValue) => handleConcurrencyChange(newValue)}
                min={1}
                max={20}
                valueLabelDisplay="off"
                disabled={allDisabled}
                className="w-full"
                sx={SLIDER_SX}
              />
            </div>
            <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
              <div className="flex items-start gap-2">
                <img
                  src="/images/genpt/throttle/icons/info.svg"
                  alt="Info"
                  className="w-4 h-4 object-contain flex-shrink-0 mt-0.5"
                />
                <div className="text-xs text-gray-700">
                  Higher concurrency speeds up testing but may lead to higher
                  load on the asset. Lower concurrency is safer for fragile or
                  resource-limited targets.
                </div>
              </div>
            </div>
          </div>

          {/* Request Timeout */}
          <div className="py-0.5 px-2 bg-white">
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2">
                <img
                  src="/images/genpt/throttle/icons/timeout.svg"
                  alt="Timeout"
                  className="w-4 h-4 object-contain flex-shrink-0"
                />
                <div className="font-semibold text-black">
                  Request Timeout
                </div>
              </div>
              <div className="font-semibold !text-[16px] text-gray-700 bg-[#F6F9FC] px-2 py-1">
                <span style={{ fontWeight: 700 }}>
                  {String(requestTimeout).padStart(2, '0')}
                </span>{' '}
                <span className="text-sm" style={{ fontWeight: 300 }}>
                  sec
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-1">
              How long the system waits for a response before marking a
              request as failed.
            </div>
            <div>
              <Slider
                value={requestTimeout}
                onChange={(e, newValue) => handleTimeoutChange(newValue)}
                min={1}
                max={10}
                valueLabelDisplay="off"
                disabled={allDisabled}
                className="w-full"
                sx={SLIDER_SX}
              />
            </div>
            <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-2">
              <div className="flex items-start gap-2">
                <img
                  src="/images/genpt/throttle/icons/info.svg"
                  alt="Info"
                  className="w-4 h-4 object-contain flex-shrink-0 mt-0.5"
                />
                <div className="text-xs text-gray-700">
                  A longer timeout helps with slow servers. A shorter timeout
                  speeds up the test on fast servers.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={'!mt-4 border-t-2 border-[#EFEFEF]'} />

        {/* Action Buttons (only when not read-only) */}
        {!isReadOnly && (
          <div className="flex justify-start gap-3 mt-6">
            {onStartTest && !scheduleOnly ? (
              <>
                <Button
                  onClick={handleStartTestClick}
                  disabled={buttonsDisabled}
                  className={`flex items-center gap-2 px-4 py-3 ${
                    buttonsDisabled
                      ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                      : '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
                  }`}
                >
                  START TEST
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleContinueClick}
                    disabled={scheduleButtonDisabled}
                    variant="outlined"
                    className={`flex items-center gap-2 px-4 py-3 ${
                      scheduleButtonDisabled
                        ? '!border-gray-300 !text-gray-500 cursor-not-allowed'
                        : '!border-primary-500 !text-primary-500 hover:!bg-primary-50 cursor-pointer'
                    }`}
                  >
                    START & SCHEDULE
                  </Button>
                  {schedulingLocked && <FeatureLockIcon testId="start-and-schedule-lock" />}
                </div>
              </>
            ) : (
              <Button
                onClick={handleContinueClick}
                disabled={buttonsDisabled}
                className={`flex items-center gap-2 px-4 py-3 ${
                  buttonsDisabled
                    ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                    : '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
                }`}
              >
                {primaryBtnText}
              </Button>
            )}
          </div>
        )}

        {/* High Rate Limit Confirmation Modal */}
        {showHighRateModal && (
          <div className="fixed inset-0 bg-[#00000080] flex items-center justify-center z-50 shadow-lg rounded-lg">
            <div className="bg-white rounded-lg p-6 max-w-lg mx-4">
              <div className="flex items-center gap-3 mb-4">
                <WarningAmberIcon className="text-[#F65C5C] text-2xl" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Confirm Throttling Settings
                </h3>
              </div>
              <p className="text-gray-700 mb-6">
                You're about to run this test with a request rate of{' '}
                <strong>120+ req/s</strong>, exceeding recommended thresholds
                and potentially impacting the target environment.
              </p>
              <div className="my-1 rounded-lg border border-[#F65C5C]/40 bg-[#F65C5C]/10 p-2 text-black">
                <div>
                  <span className="font-semibold">Warning:</span> These
                  settings may:
                </div>
                <ul className="list-disc list-inside ml-2 mt-1 space-y-1">
                  <li>
                    Trigger WAF to restricted or block our IP.
                  </li>
                  <li>
                    Increase traffic and impact application's performance.
                  </li>
                  <li>Produce incomplete or inaccurate results.</li>
                </ul>
              </div>
              <div className="my-4">
                Are you sure you want to proceed with these settings?
              </div>
              <div className="flex justify-center gap-3">
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowHighRateModal(false);
                    if (pendingAction) {
                      pendingAction();
                      setPendingAction(null);
                    }
                  }}
                >
                  Continue
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowHighRateModal(false);
                    setPendingAction(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ThrottlingSetup;
